import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router";
import type { AnalyticsEventCategory } from "src/analytics/analyticsEventTypes";
import { RoundRadio } from "src/components/RadioGroup/RoundRadio";
import { TransitRadioIcon } from "src/components/RadioGroup/TransitRadioIcon";
import color from "src/design-system/tokens/color";
import { TransitIcons } from "src/domain/SearchResultsScreen/SearchResult/TransitIcons";
import messages from "src/domain/SearchResultsScreen/SearchResultsContent/SearchResultsContent.messages";
import type { SearchResultViewModel } from "src/domain/SearchResultsScreen/createSearchResultViewModel";
import useSearch from "src/utils/hooks/useSearch";
import { tripHashFromSearchResponse } from "src/utils/location/createTripHashForCard";
import { hashChange } from "src/utils/location/hashChange";
import { useSkipToFlightSegment } from "src/utils/hooks/useSkipToFlightSegment";
import { getSchedulesPaintPerformanceKey } from "src/utils/performance";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import ChevronRight from "src/design-system/icons/ChevronRight.svg?react";
import { iconSize } from "src/design-system/tokens/iconSize";
import FuelPump from "src/design-system/icons/FuelPump.svg?react";
import { tw } from "src/design-system/tw";
import { useHoveredIndex } from "../../../../HoveredIndexProvider";
import { formatDistance } from "../../../../components/FormattedDistance/FormattedDistance";
import { FormattedDuration } from "../../../../components/FormattedDuration/FormattedDuration";
import { FormattedPriceRange } from "../../../../components/FormattedPriceRange/FormattedPriceRange";

import { useLayout } from "../../../../utils/hooks/useLayout";
import { useTypedLocation } from "../../../../utils/hooks/useTypedLocation";
import useUser from "../../../../utils/hooks/useUser";

type SearchResultTweakedProps = {
  analyticsCategory?: AnalyticsEventCategory;
  className?: string;
  routeLabel: string;
  selectedRouteIndex?: string;
  isRadioHidden?: boolean;
  $size?: "sm" | "md";
  $border?: boolean;
  isSearchScreen?: boolean;
} & SearchResultViewModel;

export function TripSearchResult({
  analyticsCategory = "SearchResults",
  ...props
}: SearchResultTweakedProps) {
  const intl = useIntl();
  const { distanceSystem } = useUser();
  const { setHoveredRouteIndex } = useHoveredIndex();
  const { searchResponse } = useSearch();
  const layout = useLayout();
  const singleFlightSegmentIndex = useSkipToFlightSegment(props.routeIndex);

  function handleMouseEnter() {
    setHoveredRouteIndex(props.routeIndex);
  }

  // Skip straight to segment screen if only one segment in the route or one major flight.
  let segmentIndex: null | number = null;
  if (props.segments.length === 1) {
    segmentIndex = 0;
  } else if (singleFlightSegmentIndex !== undefined) {
    segmentIndex = singleFlightSegmentIndex;
  }
  const suffix = segmentIndex !== null ? `/s/${segmentIndex}` : "";
  const tripHash = tripHashFromSearchResponse(searchResponse);
  const hash = props.isSearchScreen
    ? `#r/${props.canonicalName}${suffix}`
    : `${tripHash}/r/${props.canonicalName}${suffix}`;
  const location = useTypedLocation();
  const { state, ...to } = hashChange(hash, location);

  // Handler for react-router navigation on an anchor tag.
  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (segmentIndex !== null) {
      performance.mark(
        getSchedulesPaintPerformanceKey(
          "NavigateStart",
          props.routeIndex,
          segmentIndex,
        ),
      );
    }
    sendAnalyticsInteractionEvent({
      category: analyticsCategory,
      action: "Click:Result",
      label: props.routeLabel,
    });
  }

  const majorModes = props.segments
    .filter((it) => it.isMajor)
    .map((it) => it.transitMode);

  let resultTitle = props.title;

  const isDriveRoute = props.canonicalName === "Drive";
  // A drive route should only have one segment, which we can get the distance from.
  if (isDriveRoute && props.totalDistance) {
    const distance = formatDistance(props.totalDistance, distanceSystem, intl);
    resultTitle = `${props.title} ${distance}`;
  }

  useEffect(() => {
    return () => {
      setHoveredRouteIndex(undefined);
    };
  }, [setHoveredRouteIndex]);

  return (
    <div
      className={tw(
        "relative mt-2 flex cursor-pointer items-stretch font-medium",
        "rounded-tl-lg rounded-tr-md rounded-br-md rounded-bl-lg text-text-primary",
        props.$size === "sm" && "text-heading-sm leading-tight",
        props.$size === "md" &&
          "text-heading-md leading-tight md:leading-snug md:font-semibold",
      )}
      data-testid={`trip-search-result-${props.routeIndex}`}
      onMouseEnter={layout === "desktop" ? handleMouseEnter : undefined}
    >
      {!props.isRadioHidden ? (
        <div
          className={tw(
            "flex min-w-16 items-center border-2 border-transparent bg-surface",
            "mr-1 rounded-tl-lg rounded-tr-sm rounded-br-sm rounded-bl-lg",
            "focus-within:outline-2 focus-within:outline-blue-500",
            "group hover:border-neutral-300",
            props.$border && "border border-neutral-700",
          )}
        >
          <RoundRadio
            className="group-hover:bg-neutral-300"
            left
            cover
            value={props.routeIndex.toString()}
            CustomIcon={TransitRadioIcon}
          />
        </div>
      ) : null}
      <Link
        className={tw(
          "flex flex-col rounded-lg bg-surface p-3 no-underline",
          "flex-1 cursor-pointer border-2 border-transparent md:p-4",
          "mb-2 last:mb-0 hover:bg-neutral-300 active:bg-neutral-500",
          props.isRadioHidden && "border border-neutral-700 rounded-md",
          props.$border && "border border-neutral-700",
          props.className,
        )}
        onClick={handleClick}
        state={{
          fromHash: location.hash,
        }}
        to={to}
      >
        <div className="flex justify-between gap-0.5">
          <h1 className="mb-2 text-heading-sm leading-normal font-medium text-text-primary md:leading-tight">
            {resultTitle}
          </h1>
          {props.tag && (
            <div className="mb-0.5 ml-auto pt-0.5">
              <div
                className={tw(
                  "rounded-sm bg-transport-green-100 px-2 py-0 text-body-sm leading-4.5 font-normal tracking-wide text-text-success uppercase",
                )}
              >
                {intl.formatMessage(messages[props.tag])}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between gap-0.5">
          <div className="flex w-auto flex-1 text-nowrap">
            <TransitIcons modes={majorModes} />
            <div className="pt-1 pr-0 pb-0 pl-2 text-body-lg leading-4 font-normal text-text-primary">
              <FormattedDuration totalMinutes={props.durationInMinutes} />
            </div>
          </div>
          <div
            className={tw(
              "flex flex-1 flex-row-reverse items-center text-nowrap lg:justify-end",
              "gap-2 [word-wrap:inherit] lg:w-auto lg:[flex-direction:inherit]",
            )}
          >
            {props.priceRange && isDriveRoute && (
              <FuelPump className="h-5 w-5 text-transport-grey-700" />
            )}
            <div className="flex items-center gap-0.5 text-body-lg leading-tight font-medium text-text-brand">
              {props.priceRange && (
                <FormattedPriceRange {...props.priceRange} />
              )}
              <div
                className={tw(
                  "relative left-0 flex items-center justify-center",
                  "transition-[left] duration-300 ease-out hover:left-1",
                )}
                data-testid="chevron"
              >
                <ChevronRight
                  width={iconSize.sm}
                  height={iconSize.sm}
                  color={color.icon.brand}
                />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}
